import { useCallback, useEffect, useMemo, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from 'react-query';

import dpiaRequestsApi from 'config/api/dpiaRequests/dpiaRequests';
import { DPIA_CRITERIA_VERSION } from 'config/api/dpiaRequests/dpiaRequests.constants';
import type { DpiaRequestOpenResponse, OpenDpiaRequestInput } from 'config/api/dpiaRequests/dpiaRequests.types';
import MUTATION_KEYS from 'config/api/MUTATION_KEYS';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import useUnsavedChangesProtection from 'hooks/useUnsavedChangesProtection/useUnsavedChangesProtection';
import { invalidateOwnerAssetRopaQueries } from 'pages/ProcessingActivities/_shared/invalidateOwnerAssetRopaQueries';
import {
  catchHttpStatuses,
  getHttpStatus,
  HTTP_DPIA_REQUEST_UNAVAILABLE_STATUSES,
  isHttpStatus,
  isUnavailableQueryResult,
  type UnavailableQueryResult,
} from 'pages/ProcessingActivities/_shared/queryUnavailable';

import {
  isDpiaAlreadyInProgressError,
  mapFormValuesToDraftInput,
  mapRequestToFormValues,
  type DpiaNeedAssessmentFormValues,
} from './dpiaNeedAssessment.helpers';

type Params = {
  apaId: number;
  onCancelSuccess?: () => void;
  onDecideSuccess?: () => void;
  onNavigate: () => void;
  onSaveSuccess?: () => void;
  openInput?: OpenDpiaRequestInput;
  organizationId?: number;
  requestId?: number;
  useOpenEndpoint: boolean;
};

type EnvelopeQueryData = DpiaRequestOpenResponse | { alreadyInProgress: true; conflictError: unknown } | UnavailableQueryResult;

const isAlreadyInProgressEnvelope = (data: EnvelopeQueryData | undefined): data is { alreadyInProgress: true; conflictError: unknown } =>
  Boolean(data && 'alreadyInProgress' in data && data.alreadyInProgress);

const isOpenEnvelope = (data: EnvelopeQueryData | undefined): data is DpiaRequestOpenResponse =>
  Boolean(data && 'request' in data && 'context' in data);

const getEnvelopeRequestId = (data: EnvelopeQueryData | undefined) => (isOpenEnvelope(data) ? data.request.id : undefined);

const getEnvelopeError = (data: EnvelopeQueryData | undefined, queryError: unknown) => {
  if (isAlreadyInProgressEnvelope(data)) return data.conflictError;
  if (isUnavailableQueryResult(data)) return data.error;
  return queryError;
};

const useDpiaNeedAssessment = ({
  apaId,
  onCancelSuccess,
  onDecideSuccess,
  onNavigate,
  onSaveSuccess,
  openInput,
  organizationId,
  requestId,
  useOpenEndpoint,
}: Params) => {
  const queryClient = useQueryClient();
  const [formValues, setFormValues] = useState<DpiaNeedAssessmentFormValues | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  const envelopeQueryKey = useMemo(
    () =>
      useOpenEndpoint
        ? [
            QUERY_KEYS.DPIA_REQUEST_OPEN,
            organizationId,
            apaId,
            openInput?.sourceRopaReviewRoundId,
            openInput?.sourceAssetProcessingActivityReviewId,
          ]
        : [QUERY_KEYS.DPIA_REQUEST_GET, organizationId, requestId],
    [
      apaId,
      openInput?.sourceAssetProcessingActivityReviewId,
      openInput?.sourceRopaReviewRoundId,
      organizationId,
      requestId,
      useOpenEndpoint,
    ],
  );

  const envelopeQuery = useQuery(
    envelopeQueryKey,
    async () => {
      if (!organizationId) {
        throw new Error('Missing organization');
      }

      if (useOpenEndpoint) {
        try {
          const { data } = await dpiaRequestsApi.openRequest(organizationId, apaId, openInput);
          return data;
        } catch (error) {
          if (isDpiaAlreadyInProgressError(error)) {
            return { alreadyInProgress: true as const, conflictError: error };
          }
          if (isHttpStatus(error, HTTP_DPIA_REQUEST_UNAVAILABLE_STATUSES)) {
            return { unavailable: true as const, status: getHttpStatus(error) as number, error };
          }
          throw error;
        }
      }

      if (!requestId) {
        throw new Error('Missing request id');
      }

      return catchHttpStatuses(async () => {
        const { data } = await dpiaRequestsApi.getRequest(organizationId, requestId);
        return data;
      }, HTTP_DPIA_REQUEST_UNAVAILABLE_STATUSES);
    },
    {
      enabled: Boolean(organizationId) && (useOpenEndpoint ? Boolean(apaId) : Boolean(requestId)),
      retry: false,
    },
  );

  const criteriaQuery = useQuery(
    [QUERY_KEYS.DPIA_SCREENING_CRITERIA, organizationId, DPIA_CRITERIA_VERSION],
    async () => {
      const { data } = await dpiaRequestsApi.getScreeningCriteria(organizationId as number, DPIA_CRITERIA_VERSION);
      return data;
    },
    {
      enabled: Boolean(organizationId) && (useOpenEndpoint || Boolean(requestId)),
      retry: false,
    },
  );

  useEffect(() => {
    if (isOpenEnvelope(envelopeQuery.data)) {
      setFormValues(mapRequestToFormValues(envelopeQuery.data.request));
      setIsDirty(false);
    }
  }, [envelopeQuery.data]);

  const updateQueryCache = useCallback(
    (next: DpiaRequestOpenResponse) => {
      queryClient.setQueryData(envelopeQueryKey, next);
      setFormValues(mapRequestToFormValues(next.request));
    },
    [envelopeQueryKey, queryClient],
  );

  const mergeRequestIntoCache = useCallback(
    (savedRequest: DpiaRequestOpenResponse['request']) => {
      if (!isOpenEnvelope(envelopeQuery.data)) return;
      updateQueryCache({ ...envelopeQuery.data, request: savedRequest });
    },
    [envelopeQuery.data, updateQueryCache],
  );

  const saveMutation = useMutation(
    MUTATION_KEYS.DPIA_REQUEST_SAVE_DRAFT,
    async (values: DpiaNeedAssessmentFormValues) => {
      const currentRequestId = getEnvelopeRequestId(envelopeQuery.data);
      if (!organizationId || !currentRequestId) {
        throw new Error('Missing request context');
      }

      const { data } = await dpiaRequestsApi.saveDraft(organizationId, currentRequestId, mapFormValuesToDraftInput(values));
      return data;
    },
    {
      onSuccess: savedRequest => {
        mergeRequestIntoCache(savedRequest);
        onSaveSuccess?.();
      },
    },
  );

  const decideRequiredMutation = useMutation(
    MUTATION_KEYS.DPIA_REQUEST_DECIDE_REQUIRED,
    async (values: DpiaNeedAssessmentFormValues) => {
      const currentRequestId = getEnvelopeRequestId(envelopeQuery.data);
      if (!organizationId || !currentRequestId || !values.decision) {
        throw new Error('Missing request context');
      }

      const { data } = await dpiaRequestsApi.decideRequired(organizationId, currentRequestId, {
        ...mapFormValuesToDraftInput(values),
        decisionComment: values.decisionComment,
      });
      return data;
    },
    {
      onSuccess: savedRequest => {
        mergeRequestIntoCache(savedRequest);
        invalidateOwnerAssetRopaQueries(queryClient, organizationId);
        onDecideSuccess?.();
      },
    },
  );

  const decideNotRequiredMutation = useMutation(
    MUTATION_KEYS.DPIA_REQUEST_DECIDE_NOT_REQUIRED,
    async (values: DpiaNeedAssessmentFormValues) => {
      const currentRequestId = getEnvelopeRequestId(envelopeQuery.data);
      if (!organizationId || !currentRequestId || !values.decision) {
        throw new Error('Missing request context');
      }

      const { data } = await dpiaRequestsApi.decideNotRequired(organizationId, currentRequestId, {
        ...mapFormValuesToDraftInput(values),
        decisionComment: values.decisionComment,
      });
      return data;
    },
    {
      onSuccess: savedRequest => {
        mergeRequestIntoCache(savedRequest);
        invalidateOwnerAssetRopaQueries(queryClient, organizationId);
        onDecideSuccess?.();
      },
    },
  );

  const cancelMutation = useMutation(
    MUTATION_KEYS.DPIA_REQUEST_CANCEL,
    async (cancellationComment: string) => {
      const currentRequestId = getEnvelopeRequestId(envelopeQuery.data);
      if (!organizationId || !currentRequestId) {
        throw new Error('Missing request context');
      }

      const { data } = await dpiaRequestsApi.cancelRequest(organizationId, currentRequestId, { cancellationComment });
      return data;
    },
    {
      onSuccess: cancelledRequest => {
        mergeRequestIntoCache(cancelledRequest);
        onCancelSuccess?.();
      },
    },
  );

  const patchFormValues = useCallback((patch: Partial<DpiaNeedAssessmentFormValues>) => {
    setFormValues(current => (current ? { ...current, ...patch } : current));
    setIsDirty(true);
  }, []);

  const toggleCriterion = useCallback((code: DpiaNeedAssessmentFormValues['selectedCriteria'][number]) => {
    setFormValues(current => {
      if (!current) return current;

      const selected = current.selectedCriteria.includes(code)
        ? current.selectedCriteria.filter(item => item !== code)
        : [...current.selectedCriteria, code];

      return { ...current, selectedCriteria: selected };
    });
    setIsDirty(true);
  }, []);

  const handleSaveForNavigation = useCallback(async () => {
    if (!formValues) return;
    await saveMutation.mutateAsync(formValues);
    setIsDirty(false);
  }, [formValues, saveMutation]);

  const { allowNextNavigation, handleNavigation, NavigationPrompt } = useUnsavedChangesProtection({
    enabled: Boolean(formValues),
    isDirty,
    onNavigate,
    onSave: handleSaveForNavigation,
  });

  useEffect(() => {
    if (saveMutation.isSuccess) {
      setIsDirty(false);
    }
  }, [saveMutation.isSuccess]);

  const isDeciding = decideRequiredMutation.isLoading || decideNotRequiredMutation.isLoading;
  const alreadyInProgress = isAlreadyInProgressEnvelope(envelopeQuery.data);
  const request = isOpenEnvelope(envelopeQuery.data) ? envelopeQuery.data.request : undefined;
  const context = isOpenEnvelope(envelopeQuery.data) ? envelopeQuery.data.context : undefined;
  const isLoading = envelopeQuery.isLoading || criteriaQuery.isLoading;
  const isError = envelopeQuery.isError || criteriaQuery.isError;
  const envelopeError = getEnvelopeError(envelopeQuery.data, envelopeQuery.error);

  return useMemo(
    () => ({
      allowNextNavigation,
      alreadyInProgress,
      cancelMutation,
      context,
      criteria: criteriaQuery.data?.criteria ?? [],
      decideNotRequiredMutation,
      decideRequiredMutation,
      envelopeError,
      formValues,
      handleNavigation,
      isDeciding,
      isDirty,
      isError,
      isLoading,
      isSaving: saveMutation.isLoading,
      NavigationPrompt,
      patchFormValues,
      refetch: envelopeQuery.refetch,
      request,
      saveMutation,
      toggleCriterion,
    }),
    [
      allowNextNavigation,
      alreadyInProgress,
      cancelMutation,
      context,
      criteriaQuery.data?.criteria,
      decideNotRequiredMutation,
      decideRequiredMutation,
      envelopeError,
      envelopeQuery.refetch,
      formValues,
      handleNavigation,
      isDeciding,
      isDirty,
      isError,
      isLoading,
      NavigationPrompt,
      patchFormValues,
      request,
      saveMutation,
      toggleCriterion,
    ],
  );
};

export default useDpiaNeedAssessment;
